import React, { useState, useEffect, useRef } from 'react';
import checkeredPattern from './checkers.png';
import sampleIcon from './sampleIcon.png';
import straightLineIcon from './straightLine.png';
import squiggleLineIcon from './squiggleLine.png';
import horizontalIcon from './horizontal.png';
import undoIcon from './undoIcon.png';
import squareLineIcon from './squareLine.png';
import circleLineIcon from './circleLine.png';
import eraseIcon from './eraseIcon.png';
import transformIcon from './transform.png';
import MobileColorPicker from './MobileColorPicker';
import HsvJitterModal from './HsvJitterModal';
import blendingIcon from './blending.png'; // Blending icon replaces Clear Canvas button

const JITTER_STORAGE_KEY = 'hsvJitter';

const loadStoredJitter = () => {
    try {
        const raw = localStorage.getItem(JITTER_STORAGE_KEY);
        if (!raw) return { h: 0, s: 0, v: 0, mode: 'stroke' };
        const parsed = JSON.parse(raw);
        const clamp = (n, max) => Math.max(0, Math.min(max, Number(n) || 0));
        const mode = parsed.mode === 'paint' ? 'paint' : 'stroke';
        return {
            h: clamp(parsed.h, 180),
            s: clamp(parsed.s, 100),
            v: clamp(parsed.v, 100),
            mode,
        };
    } catch (_e) {
        return { h: 0, s: 0, v: 0, mode: 'stroke' };
    }
};

const lineModes = [
    { id: 'straight', icon: straightLineIcon },
    { id: 'squiggle', icon: squiggleLineIcon },
    { id: 'square', icon: squareLineIcon },
    { id: 'circle', icon: circleLineIcon },
    { id: 'horizontal', icon: horizontalIcon },
];

const hexToRGB = (hex) => {
    let r = 0, g = 0, b = 0;
    if (hex.length === 4) {
        r = parseInt(hex[1] + hex[1], 16);
        g = parseInt(hex[2] + hex[2], 16);
        b = parseInt(hex[3] + hex[3], 16);
    } else if (hex.length === 7) {
        r = parseInt(hex[1] + hex[2], 16);
        g = parseInt(hex[3] + hex[4], 16);
        b = parseInt(hex[5] + hex[6], 16);
    }
    return { r, g, b };
};

const brushSizes = ['XS', 'S', 'M', 'L', 'XL'];
const sizeToDimensionMap = { XS: 10, S: 13, M: 16, L: 20, XL: 23 };
const alphas = [0.1, 0.3, 1.0];

// width × height in CSS pixels for the small preview rectangle inside each
// ratio button. Picked so the largest dimension is 18px and the other is
// proportional to the ratio.
const ratioOptions = [
    { id: 'landscape', label: 'Landscape', previewW: 18, previewH: 12 },
    { id: 'square',    label: 'Square',    previewW: 16, previewH: 16 },
    { id: 'portrait',  label: 'Portrait',  previewW: 12, previewH: 18 },
];

export const ColorPalette = ({
    color,
    setColor,
    onBrushSizeChange,
    onAlphaChange,
    onUndo,
    sampleMode,
    setSampleMode,
    onLineModeChange,
    eraseMode,
    onEraseModeChange,
    blendingMode,
    onClearCanvas, // kept for future re-add of the button
    onTransformModeChange,
    onSelectionModeChange,
    onBlendingModeChange,
    isTablet,
    isPhone,
    flipped,
    onToggleFlipped,
    canvasRatio,
    onRatioChange,
    onJitterChange,
}) => {
    const [selectedBrushSize, setSelectedBrushSize] = useState('');
    const [selectedAlpha, setSelectedAlpha] = useState(0);
    const [lineMode, setLineMode] = useState('squiggle');
    const [selectedLineMode, setSelectedLineMode] = useState('squiggle');
    const [pickerOpen, setPickerOpen] = useState(false);
    const [transformMode, setTransformMode] = useState(false);
    const [selectionMode, setSelectionMode] = useState(false);
    const [draftColor, setDraftColor] = useState(color);
    const [ratioMenuOpen, setRatioMenuOpen] = useState(false);
    const ratioMenuRef = useRef(null);
    const [lineMenuOpen, setLineMenuOpen] = useState(false);
    const lineMenuRef = useRef(null);
    const [jitterModalOpen, setJitterModalOpen] = useState(false);
    const initialJitter = loadStoredJitter();
    const [hueJitter, setHueJitter] = useState(initialJitter.h);
    const [satJitter, setSatJitter] = useState(initialJitter.s);
    const [valJitter, setValJitter] = useState(initialJitter.v);
    const [jitterMode, setJitterMode] = useState(initialJitter.mode);

    useEffect(() => {
        try {
            localStorage.setItem(
                JITTER_STORAGE_KEY,
                JSON.stringify({ h: hueJitter, s: satJitter, v: valJitter, mode: jitterMode }),
            );
        } catch (_e) {}
        if (typeof onJitterChange === 'function') {
            onJitterChange({ h: hueJitter, s: satJitter, v: valJitter, mode: jitterMode });
        }
    }, [hueJitter, satJitter, valJitter, jitterMode, onJitterChange]);

    const jitterActive = hueJitter > 0 || satJitter > 0 || valJitter > 0;

    useEffect(() => {
        if (!ratioMenuOpen) return;
        const handlePointerDown = (e) => {
            if (ratioMenuRef.current && !ratioMenuRef.current.contains(e.target)) {
                setRatioMenuOpen(false);
            }
        };
        document.addEventListener('pointerdown', handlePointerDown);
        return () => document.removeEventListener('pointerdown', handlePointerDown);
    }, [ratioMenuOpen]);

    useEffect(() => {
        if (!lineMenuOpen) return;
        const handlePointerDown = (e) => {
            if (lineMenuRef.current && !lineMenuRef.current.contains(e.target)) {
                setLineMenuOpen(false);
            }
        };
        document.addEventListener('pointerdown', handlePointerDown);
        return () => document.removeEventListener('pointerdown', handlePointerDown);
    }, [lineMenuOpen]);

    const currentRatio = ratioOptions.find((r) => r.id === canvasRatio) || ratioOptions[1];
    const currentLineMode = lineModes.find((m) => m.id === selectedLineMode) || lineModes[1];

    const handleToggleBlendingMode = () => {
        if (typeof onBlendingModeChange === 'function') onBlendingModeChange(!blendingMode);
    };

    const handleClick = () => {
        setDraftColor(color);
        setPickerOpen(true);
    };

    const handleClose = () => {
        setPickerOpen(false);
        setColor(draftColor, /* skipHistory */ false);
    };

    const handleChange = (hex) => {
        setDraftColor(hex);
        setColor(hex, /* skipHistory */ true);
    };

    useEffect(() => {
        setColor('#FF0000', /* skipHistory */ true);
        setSelectedAlpha(1.0);
        onAlphaChange(1.0);
        setSelectedBrushSize('XS');
        onBrushSizeChange('XS');
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleSelectBrushSize = (size) => {
        setSelectedBrushSize(size);
        onBrushSizeChange(size);
    };

    const handleSelectAlpha = (alpha) => {
        setSelectedAlpha(alpha);
        onAlphaChange(alpha);
    };

    const handleSelectLineMode = (mode) => {
        setSelectedLineMode(mode);
        setLineMode(mode);
        onLineModeChange(mode);
    };

    const handleToggleSampleMode = () => setSampleMode(!sampleMode);

    // Selection and transform both claim the canvas gesture, so they are
    // mutually exclusive — enabling one turns the other off.
    const handleToggleSelectionMode = () => {
        const next = !selectionMode;
        setSelectionMode(next);
        if (next && transformMode) {
            setTransformMode(false);
            onTransformModeChange(false);
        }
        if (typeof onSelectionModeChange === 'function') onSelectionModeChange(next);
    };

    // Toggles move/transform mode on and off.
    const handleCycleTransformMode = () => {
        const next = !transformMode;
        setTransformMode(next);
        onTransformModeChange(next);
        if (next && selectionMode) {
            setSelectionMode(false);
            if (typeof onSelectionModeChange === 'function') onSelectionModeChange(false);
        }
    };

    const handleToggleEraseMode = () => {
        onEraseModeChange(!eraseMode);
    };

    const handleUndo = () => onUndo?.();

    const alphaBackgroundStyle = (alpha) => {
        const { r, g, b } = hexToRGB('#FFFFFF');
        let displayAlpha = alpha;
        if (alpha === 1) displayAlpha = 0.9;
        else if (alpha === 0.1) displayAlpha = 0.2;
        else if (alpha === 0.3) displayAlpha = 0.4;
        return {
            width: '30px',
            height: '30px',
            borderRadius: '50%',
            backgroundImage: `linear-gradient(rgba(${r}, ${g}, ${b}, ${displayAlpha}), rgba(${r}, ${g}, ${b}, ${displayAlpha})), url(${checkeredPattern})`,
            border: alpha === selectedAlpha ? '2px solid cyan' : '2px solid grey',
            cursor: 'pointer',
        };
    };

    const buttonBase = {
        width: '30px',
        height: '30px',
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        margin: '4px',
        background: 'white',
    };

    const getBlendingModeButtonStyle = () => ({
        ...buttonBase,
        border: blendingMode ? '2px solid cyan' : '2px solid grey',
    });

    const getEraseModeButtonStyle = () => ({
        ...buttonBase,
        border: eraseMode ? '2px solid cyan' : '2px solid grey',
    });

    const getSampleModeButtonStyle = () => ({
        ...buttonBase,
        border: sampleMode ? '2px solid cyan' : '2px solid grey',
    });

    const getTransformCycleButtonStyle = () => ({
        ...buttonBase,
        border: transformMode ? '2px solid cyan' : '2px solid grey',
    });

    const getSelectionModeButtonStyle = () => ({
        ...buttonBase,
        border: selectionMode ? '2px solid cyan' : '2px solid grey',
    });

    const getLineModeButtonStyle = (mode) => ({
        ...buttonBase,
        border: mode === selectedLineMode ? '2px solid cyan' : '2px solid grey',
        margin: '1px',
    });

    const swatchStyle = {
        width: '30px',
        height: '30px',
        border: '1px solid grey',
        background: color,
        cursor: 'pointer',
        marginRight: '5px',
    };

    return (
        <div className="palette-z-index">
            <div style={{ display: 'flex', justifyContent: 'center', gap: '5px', marginBottom: '2px', alignItems: 'center' }}>
                <div style={swatchStyle} onClick={handleClick} />
                <div style={{ width: '2px', backgroundColor: 'gray', height: '20px', margin: '0 0px' }} />
                {brushSizes.map((size) => {
                    const dimension = sizeToDimensionMap[size];
                    return (
                        <div
                            key={size}
                            onClick={() => handleSelectBrushSize(size)}
                            style={{
                                width: `${dimension}px`,
                                height: `${dimension}px`,
                                borderRadius: '50%',
                                backgroundColor: '#000',
                                border: size === selectedBrushSize ? '2px solid cyan' : '2px solid black',
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                margin: '4px',
                            }}
                        />
                    );
                })}

                <MobileColorPicker open={pickerOpen} color={color} onChange={handleChange} onClose={handleClose} />

                <div style={{ width: '2px', backgroundColor: 'gray', height: '20px', margin: '0 0px' }} />
                {alphas.map((alpha) => (
                    <div key={alpha} onClick={() => handleSelectAlpha(alpha)} style={alphaBackgroundStyle(alpha)} />
                ))}

                <div style={{ width: '2px', backgroundColor: 'gray', height: '20px', margin: '0 0px' }} />

                <div
                    onClick={handleUndo}
                    style={{
                        width: '30px',
                        height: '30px',
                        borderRadius: '50%',
                        backgroundColor: 'white',
                        border: '2px solid grey',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '12px',
                    }}
                >
                    <img src={undoIcon} width="20" height="20" alt="undo" />
                </div>
            </div>

            <div style={{ display: 'flex', justifyContent: 'center', gap: '2px', marginBottom: '2px', alignItems: 'center' }}>
                {isTablet && (
                    <div
                        style={{
                            ...buttonBase,
                            border: flipped ? '2px solid cyan' : '2px solid grey',
                            fontSize: '20px',
                            lineHeight: 1,
                            color: '#222',
                        }}
                        onClick={onToggleFlipped}
                        title="Flip canvas horizontally (view only)"
                    >
                        ⇄
                    </div>
                )}
                {/* REPLACED: Clear Canvas button -> Blending button in this slot */}
                <div style={getBlendingModeButtonStyle()} onClick={handleToggleBlendingMode} title="Blending mode (per-stroke)">
                    <img src={blendingIcon} alt="blending" style={{ width: '20px', height: '20px' }} />
                </div>

                {isTablet && (
                    <div
                        style={{
                            ...buttonBase,
                            border: jitterActive ? '2px solid cyan' : '2px solid grey',
                            fontSize: '11px',
                            fontWeight: 700,
                            color: '#222',
                            position: 'relative',
                        }}
                        onClick={() => setJitterModalOpen(true)}
                        title="Color jitter (per-stroke HSV randomization)"
                    >
                        HSV
                    </div>
                )}

                <div style={{ width: '3px', height: '30px', margin: '2px' }} />

                {isPhone ? (
                    <div
                        ref={lineMenuRef}
                        style={{ position: 'relative', display: 'inline-flex' }}
                    >
                        <div
                            style={{
                                ...buttonBase,
                                margin: '1px',
                                border: lineMenuOpen ? '2px solid cyan' : '2px solid grey',
                            }}
                            onClick={() => setLineMenuOpen((v) => !v)}
                            title={`${currentLineMode.id} line — tap to change`}
                        >
                            <img
                                src={currentLineMode.icon}
                                alt={`${currentLineMode.id} line`}
                                style={{ width: '18px', height: '18px' }}
                            />
                        </div>

                        {lineMenuOpen && (
                            <div
                                style={{
                                    position: 'absolute',
                                    top: 'calc(100% + 6px)',
                                    left: '50%',
                                    transform: 'translateX(-50%)',
                                    display: 'flex',
                                    flexDirection: 'row',
                                    backgroundColor: 'white',
                                    border: '1px solid black',
                                    gap: '4px',
                                    padding: '4px',
                                    borderRadius: '8px',
                                    boxShadow: '0 2px 8px rgba(0,0,0,0.12)',
                                    zIndex: 10,
                                }}
                            >
                                {lineModes.map(({ id, icon }) => (
                                    <div
                                        key={id}
                                        style={{
                                            ...buttonBase,
                                            margin: 0,
                                            border: id === selectedLineMode ? '2px solid cyan' : '2px solid grey',
                                        }}
                                        onClick={() => {
                                            setLineMenuOpen(false);
                                            handleSelectLineMode(id);
                                        }}
                                        title={`${id} line`}
                                    >
                                        <img src={icon} alt={`${id} line`} style={{ width: '18px', height: '18px' }} />
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                ) : (
                    lineModes.map(({ id, icon }) => (
                        <div key={id} style={getLineModeButtonStyle(id)} onClick={() => handleSelectLineMode(id)}>
                            <img src={icon} alt={`${id} line`} style={{ width: '18px', height: '18px' }} />
                        </div>
                    ))
                )}

                <div style={{ width: '3px', height: '30px', margin: '2px' }} />

                <div style={getEraseModeButtonStyle()} onClick={handleToggleEraseMode}>
                    <img src={eraseIcon} alt="erase" style={{ width: '20px', height: '20px' }} />
                </div>

                <div style={{ width: '3px', height: '30px', margin: '0px' }} />

                <div style={getSampleModeButtonStyle()} onClick={handleToggleSampleMode}>
                    <img src={sampleIcon} alt="sample" style={{ width: 'auto', height: '17px' }} />
                </div>

                <div style={{ width: '3px', height: '30px', margin: '0px' }} />

                <div
                    style={getTransformCycleButtonStyle()}
                    onClick={handleCycleTransformMode}
                    title={
                        transformMode
                            ? 'Move mode — tap to turn off'
                            : 'Tap to enable move/transform'
                    }
                >
                    <img src={transformIcon} alt="transform" style={{ width: 'auto', height: '17px' }} />
                </div>

                <div style={{ width: '3px', height: '30px', margin: '0px' }} />

                <div
                    style={getSelectionModeButtonStyle()}
                    onClick={handleToggleSelectionMode}
                    title={
                        selectionMode
                            ? 'Selection tool on — tap to turn off'
                            : 'Selection tool: drag a box, then move/rotate/scale it'
                    }
                >
                    <div
                        style={{
                            width: '16px',
                            height: '12px',
                            border: '1.5px dashed #222',
                            boxSizing: 'border-box',
                        }}
                    />
                </div>

                <div style={{ width: '3px', height: '30px', margin: '0px' }} />

                <div
                    ref={ratioMenuRef}
                    style={{ position: 'relative', display: 'inline-flex' }}
                >
                    <div
                        style={{
                            ...buttonBase,
                            border: ratioMenuOpen ? '2px solid cyan' : '2px solid grey',
                        }}
                        onClick={() => setRatioMenuOpen((v) => !v)}
                        title={`${currentRatio.label} canvas — tap to change`}
                    >
                        <div
                            style={{
                                width: `${currentRatio.previewW}px`,
                                height: `${currentRatio.previewH}px`,
                                border: '1.5px solid #222',
                                boxSizing: 'border-box',
                                background: 'white',
                            }}
                        />
                    </div>

                    {ratioMenuOpen && (
                        <div
                            style={{
                                position: 'absolute',
                                top: 'calc(100% + 6px)',
                                left: '50%',
                                transform: 'translateX(-50%)',
                                display: 'flex',
                                flexDirection: 'row',
                                backgroundColor: 'white',
                                border: '1px solid black',
                                gap: '4px',
                                padding: '4px',
                                borderRadius: '8px',
                                boxShadow: '0 2px 8px rgba(0,0,0,0.12)',
                                zIndex: 10,
                            }}
                        >
                            {ratioOptions.map(({ id, label, previewW, previewH }) => (
                                <div
                                    key={id}
                                    style={{
                                        ...buttonBase,
                                        margin: 0,
                                        border: id === canvasRatio ? '2px solid cyan' : '2px solid grey',
                                    }}
                                    onClick={() => {
                                        setRatioMenuOpen(false);
                                        if (onRatioChange) onRatioChange(id);
                                    }}
                                    title={`${label} canvas`}
                                >
                                    <div
                                        style={{
                                            width: `${previewW}px`,
                                            height: `${previewH}px`,
                                            border: '1.5px solid #222',
                                            boxSizing: 'border-box',
                                            background: 'white',
                                        }}
                                    />
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>

            <HsvJitterModal
                open={jitterModalOpen}
                onClose={() => setJitterModalOpen(false)}
                color={color}
                hueJitter={hueJitter}
                satJitter={satJitter}
                valJitter={valJitter}
                setHueJitter={setHueJitter}
                setSatJitter={setSatJitter}
                setValJitter={setValJitter}
                mode={jitterMode}
                setMode={setJitterMode}
            />
        </div>
    );
};
